import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import clienteService, { type PadronAfipCliente } from '../../../services/clienteService';
import estrategiaService from '../../../services/estrategiaService';
import variablesSistemaService from '../../../services/variablesSistemaService';
import localidadService from '../../../services/localidadService';
import LocalidadSearch from '../../../components/LocalidadSearch';
import { normalizeCuitForStorage } from '../../../utils/formatters';
import type { POSCliente } from './types';

const esConsumidorFinalIva = (tipo: string) => {
  const t = tipo.trim().toUpperCase();
  return t === 'C' || t === 'CF';
};

const TIPO_IVA_OPCIONES: { value: string; label: string }[] = [
  { value: 'I', label: 'Responsable Inscripto' },
  { value: 'C', label: 'Consumidor Final' },
  { value: 'M', label: 'Monotributo' },
  { value: 'E', label: 'Exento' },
  { value: 'N', label: 'No Categorizado' },
];

const TIPOS_IVA_EONE = new Set(['I', 'C', 'M', 'E', 'N']);

interface LocalidadSel {
  codigoCiudad: number;
  nombreCiudad?: string;
  nombreProvincia?: string;
}

const normalizarTexto = (value: string) =>
  value
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

function armarDireccionPadronAfip(padron: PadronAfipCliente): string {
  const partes: string[] = [];
  const calle = (padron.direccion || '').trim();
  if (calle) partes.push(calle);

  const localidad = (padron.localidad || '').trim();
  const cp = (padron.codigoPostal || '').trim();
  const provincia = (padron.provincia || '').trim();

  let lugar = '';
  if (localidad && cp) lugar = `${localidad} (${cp})`;
  else if (localidad) lugar = localidad;
  else if (cp) lugar = `CP ${cp}`;
  if (lugar && provincia) lugar = `${lugar}, ${provincia}`;
  else if (provincia) lugar = provincia;
  if (lugar) partes.push(lugar);

  return partes.join(', ');
}

async function buscarLocalidadDesdePadron(
  nombreLocalidad?: string | null,
  nombreProvincia?: string | null,
): Promise<LocalidadSel | null> {
  const term = (nombreLocalidad || '').trim();
  if (!term) return null;
  try {
    const response = await localidadService.getLocalidades({
      searchTerm: term,
      pageNumber: 1,
      pageSize: 50,
    });
    const items = response.items || [];
    if (items.length === 0) return null;

    const target = normalizarTexto(term);
    const exactas = items.filter((i) => normalizarTexto(i.nombreCiudad || '') === target);
    const candidatas = exactas.length ? exactas : items;
    const provincia = (nombreProvincia || '').trim();

    const mapLocalidad = (loc: (typeof items)[number]): LocalidadSel => ({
      codigoCiudad: loc.codigoCiudad,
      nombreCiudad: loc.nombreCiudad,
      nombreProvincia: loc.nombreProvincia,
    });

    if (provincia) {
      const p = normalizarTexto(provincia);
      const porProvincia = candidatas.filter((i) => {
        const np = normalizarTexto(i.nombreProvincia || '');
        return np && (np.includes(p) || p.includes(np));
      });
      if (porProvincia.length >= 1) return mapLocalidad(porProvincia[0]);
    }

    if (candidatas.length === 1) return mapLocalidad(candidatas[0]);
    if (exactas.length === 1) return mapLocalidad(exactas[0]);
    return null;
  } catch {
    return null;
  }
}

const extractApiError = (e: unknown): string => {
  const err = e as { response?: { data?: unknown }; message?: string };
  const data = err?.response?.data;
  if (!data) return err?.message ?? 'Error al guardar el cliente';
  if (typeof data === 'string') return data;
  const d = data as Record<string, unknown>;
  if (d.message) return String(d.message);
  if (d.errors && typeof d.errors === 'object') {
    return Object.values(d.errors as Record<string, string[]>)
      .flat()
      .join(' | ');
  }
  return JSON.stringify(data).substring(0, 300);
};

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (cliente: POSCliente) => void;
}

const FacturaPOSNuevoClienteDialog: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [loadingInit, setLoadingInit] = useState(false);
  const [nroCliente, setNroCliente] = useState(0);
  const [nroCuenta, setNroCuenta] = useState<number | null>(null);
  const [nombre, setNombre] = useState('');
  const [cuit, setCuit] = useState('');
  const [dni, setDni] = useState('');
  const [tipoIva, setTipoIva] = useState('C');
  const [direccion, setDireccion] = useState('');
  const [localidad, setLocalidad] = useState<LocalidadSel | null>(null);
  const [localidadOpen, setLocalidadOpen] = useState(false);
  const [telefono1, setTelefono1] = useState('');
  const [email, setEmail] = useState('');
  const [codigoRutaDefault, setCodigoRutaDefault] = useState(1);
  const [consultandoAfip, setConsultandoAfip] = useState(false);

  const resetForm = useCallback(() => {
    setNombre('');
    setCuit('');
    setDni('');
    setTipoIva('C');
    setDireccion('');
    setLocalidad(null);
    setTelefono1('');
    setEmail('');
  }, []);

  useEffect(() => {
    if (!open) return;
    resetForm();
    setLoadingInit(true);
    void (async () => {
      try {
        const [proximo, estrategia, codigoRuta] = await Promise.all([
          clienteService.getProximoNumero(),
          estrategiaService.getEstrategia(1).catch(() => null),
          variablesSistemaService.getCodigoRuta(),
        ]);
        setNroCliente(Number(proximo) || 0);
        const cuenta = estrategia?.clientes;
        setNroCuenta(cuenta != null && Number(cuenta) > 0 ? Number(cuenta) : null);
        setCodigoRutaDefault(codigoRuta);
      } catch {
        enqueueSnackbar('No se pudo obtener el próximo número de cliente', { variant: 'warning' });
        setNroCliente(0);
        setNroCuenta(null);
      } finally {
        setLoadingInit(false);
      }
    })();
  }, [open, resetForm, enqueueSnackbar]);

  const handleConsultarAfip = async () => {
    const cuitNorm = normalizeCuitForStorage(cuit);
    if (cuitNorm.length !== 11) {
      enqueueSnackbar('Ingrese un CUIT de 11 dígitos para consultar ARCA', { variant: 'warning' });
      return;
    }

    const tieneDatos =
      Boolean(nombre.trim()) ||
      Boolean(direccion.trim()) ||
      Boolean(localidad) ||
      Boolean(dni.trim()) ||
      (Boolean(tipoIva.trim()) && !esConsumidorFinalIva(tipoIva));

    if (tieneDatos) {
      const ok = window.confirm(
        'Ya hay datos cargados en el formulario. ¿Desea sobrescribirlos con la consulta a ARCA?'
      );
      if (!ok) return;
    }

    setConsultandoAfip(true);
    try {
      const padron = await clienteService.getPadronAfip(cuitNorm);
      const tipoIvaPadron = String(padron.tipoIva || '').trim().toUpperCase();
      const direccionArmada = armarDireccionPadronAfip(padron);
      const nombrePadron =
        (padron.nombre != null && String(padron.nombre).trim()) ||
        (padron.razonSocial != null && String(padron.razonSocial).trim()) ||
        '';

      setCuit(normalizeCuitForStorage(padron.cuit) || cuitNorm);
      if (nombrePadron) setNombre(nombrePadron);
      if (direccionArmada) setDireccion(direccionArmada);
      if (TIPOS_IVA_EONE.has(tipoIvaPadron)) {
        setTipoIva(tipoIvaPadron);
        if (!esConsumidorFinalIva(tipoIvaPadron)) setDni('');
      }

      const loc = await buscarLocalidadDesdePadron(padron.localidad, padron.provincia);
      if (loc) setLocalidad(loc);

      const msg =
        (padron.mensaje && String(padron.mensaje).trim()) ||
        'Datos precargados desde ARCA';
      const avisoCiudad =
        !loc && (padron.localidad || '').trim() ? ' Seleccioná la ciudad.' : '';
      enqueueSnackbar(`${msg}${avisoCiudad}`, { variant: 'success' });
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string; errors?: { padronAfip?: string | string[] } } | string };
        message?: string;
      };
      const data = err?.response?.data;
      let messageText: string =
        (typeof data === 'object' && data && typeof data.message === 'string' ? data.message : '') ||
        (typeof data === 'string' ? data : '') ||
        err?.message ||
        '';

      const padronErr = typeof data === 'object' && data ? data.errors?.padronAfip : undefined;
      if (padronErr) {
        const detalle = Array.isArray(padronErr) ? padronErr.join('; ') : String(padronErr);
        messageText = messageText ? `${messageText}: ${detalle}` : detalle;
      }

      enqueueSnackbar(messageText || 'Error al consultar el padrón ARCA', {
        variant: 'error',
        autoHideDuration: 10000,
      });
    } finally {
      setConsultandoAfip(false);
    }
  };

  const handleSubmit = async () => {
    const nombreTrim = nombre.trim();
    if (!nombreTrim) {
      enqueueSnackbar('Ingresá el nombre del cliente', { variant: 'warning' });
      return;
    }
    if (!tipoIva.trim()) {
      enqueueSnackbar('Seleccioná el tipo de IVA', { variant: 'warning' });
      return;
    }
    if (!direccion.trim()) {
      enqueueSnackbar('Ingresá la dirección de facturación', { variant: 'warning' });
      return;
    }
    if (!localidad?.codigoCiudad || localidad.codigoCiudad <= 0) {
      enqueueSnackbar('Seleccioná la ciudad', { variant: 'warning' });
      return;
    }
    if (!nroCliente || nroCliente <= 0) {
      enqueueSnackbar('No hay número de cliente disponible. Reintentá o cargá el cliente desde el maestro.', {
        variant: 'warning',
      });
      return;
    }
    if (!nroCuenta || nroCuenta <= 0) {
      enqueueSnackbar(
        'No se encontró cuenta contable por defecto (estrategia). Configurá la estrategia o dé de alta el cliente desde Clientes.',
        { variant: 'warning' },
      );
      return;
    }

    const cuitDigits = cuit.replace(/\D/g, '');
    const dniNum = (() => {
      const s = dni.replace(/\D/g, '');
      if (!s) return null;
      const n = Number(s);
      return Number.isFinite(n) && n >= 0 ? n : null;
    })();

    const hoy = new Date().toISOString().split('T')[0];
    const tipoIvaNorm = tipoIva.trim().toUpperCase();
    const esCf = esConsumidorFinalIva(tipoIvaNorm);

    setSaving(true);
    try {
      const payload = {
        nroCliente,
        nombre: nombreTrim,
        razonSocial: nombreTrim,
        direccion: direccion.trim(),
        codigoCiudad: localidad.codigoCiudad,
        cuit: esCf ? null : (cuitDigits || null),
        dni: esCf ? dniNum : null,
        tipoIva: tipoIvaNorm,
        condVta: 'CONTADO',
        tipoCliente: 'A',
        nroCuenta,
        fechaAlta: hoy,
        vendedor: 1,
        nroCobrador: 1,
        codigoDireccion: 'FISCAL',
        codigoRuta: codigoRutaDefault,
        deposito: 0,
        telefono1: telefono1.trim() || null,
        email: email.trim().toLowerCase() || null,
        credito: 0,
        porcDesc: 0,
        porcDesc2: 0,
        porcDesc3: 0,
      };

      const creado = await clienteService.createCliente(payload);
      const nro = Number(creado.nroCliente) || nroCliente;
      let completo = creado as POSCliente;
      try {
        completo = (await clienteService.getCliente(nro)) as POSCliente;
      } catch {
        /* usar respuesta del alta */
      }
      enqueueSnackbar(`Cliente #${nro} creado`, { variant: 'success' });
      onCreated(completo);
      onClose();
    } catch (e) {
      enqueueSnackbar(extractApiError(e), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const localidadLabel = localidad
    ? [localidad.nombreCiudad, localidad.nombreProvincia].filter(Boolean).join(', ')
    : '';

  const muestraDni = esConsumidorFinalIva(tipoIva);

  const handleTipoIvaChange = (value: string) => {
    setTipoIva(value);
    if (!esConsumidorFinalIva(value)) {
      setDni('');
    }
  };

  return (
    <>
      <Dialog open={open} onClose={saving || consultandoAfip ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo cliente</DialogTitle>
        <DialogContent dividers>
          {loadingInit ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ pt: 0.5 }}>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Nº de cliente asignado: <strong>{nroCliente > 0 ? nroCliente : '—'}</strong>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                >
                  <TextField
                    label="CUIT"
                    fullWidth
                    size="small"
                    value={cuit}
                    onChange={(e) => setCuit(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void handleConsultarAfip();
                      }
                    }}
                    disabled={consultandoAfip}
                    autoFocus
                    helperText="Ingresá el CUIT y consultá ARCA para precargar los datos"
                  />
                  <Button
                    variant="outlined"
                    onClick={() => void handleConsultarAfip()}
                    disabled={consultandoAfip || saving}
                    sx={{
                      whiteSpace: 'nowrap',
                      height: 40,
                      minWidth: 140,
                      textTransform: 'none',
                      flexShrink: 0,
                    }}
                  >
                    {consultandoAfip ? <CircularProgress size={20} /> : 'Consultar ARCA'}
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Nombre"
                  required
                  fullWidth
                  size="small"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  inputProps={{ maxLength: 120 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Tipo IVA"
                  required
                  fullWidth
                  size="small"
                  value={tipoIva}
                  onChange={(e) => handleTipoIvaChange(e.target.value)}
                >
                  {TIPO_IVA_OPCIONES.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {muestraDni && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="DNI"
                    fullWidth
                    size="small"
                    value={dni}
                    onChange={(e) => setDni(e.target.value)}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  label="Dirección (facturación)"
                  required
                  fullWidth
                  size="small"
                  value={direccion}
                  onChange={(e) => setDireccion(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Ciudad"
                  required
                  fullWidth
                  size="small"
                  value={localidadLabel}
                  placeholder="Buscar localidad…"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          color="primary"
                          onClick={() => setLocalidadOpen(true)}
                          aria-label="Buscar ciudad"
                        >
                          <ArrowDropDownIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Teléfono"
                  fullWidth
                  size="small"
                  value={telefono1}
                  onChange={(e) => setTelefono1(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  fullWidth
                  size="small"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={onClose} disabled={saving || consultandoAfip}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSubmit()}
            disabled={saving || loadingInit || consultandoAfip}
          >
            {saving ? 'Guardando…' : 'Crear y seleccionar'}
          </Button>
        </DialogActions>
      </Dialog>

      <LocalidadSearch
        open={localidadOpen}
        onClose={() => setLocalidadOpen(false)}
        onSelect={(loc) => {
          setLocalidad({
            codigoCiudad: loc.codigoCiudad,
            nombreCiudad: loc.nombreCiudad,
            nombreProvincia: loc.nombreProvincia,
          });
          setLocalidadOpen(false);
        }}
      />
    </>
  );
};

export default FacturaPOSNuevoClienteDialog;
